import React from 'react';
import { SpecialEvent } from '../types';

interface SpecialCardProps {
  event: SpecialEvent;
  onClick?: () => void;
  onFavoriteToggle?: (e: React.MouseEvent) => void;
  isFavorite?: boolean;
  onCopyAddress?: (e: React.MouseEvent) => void;
  copiedId?: string | null;
  hideFavorite?: boolean;
  className?: string;
  onGetVerified?: () => void;
  distanceMiles?: number | null;
}

const WEEKDAY_LABELS = ['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'];
const LABEL_TO_DAY_NUMBER = [1, 2, 3, 4, 5, 6, 0];
const MAX_VERIFIED_CARD_DEALS = 5;
const MAX_PREVIEW_CARD_DEALS = 3;

const formatTo12Hour = (time24: string) => {
  if (!time24) return 'Anytime';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
};

const formatDistance = (miles?: number | null) => {
  if (typeof miles !== 'number' || !Number.isFinite(miles)) return '';
  if (miles < 0.1) return 'Less than 0.1 mi away';
  return `${miles.toFixed(miles < 10 ? 1 : 0)} mi away`;
};

// Accept arrays, JSON strings, or Postgres array strings like "{1,2,3}"
function coerceToArray(input: any): any[] {
  if (Array.isArray(input)) return input;

  if (typeof input === 'string') {
    const s = input.trim();

    // JSON array string e.g. '["1","2"]'
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    // Postgres array string e.g. "{1,2,3}"
    if (s.startsWith('{') && s.endsWith('}')) {
      const inner = s.slice(1, -1).trim();
      if (!inner) return [];
      return inner.split(',').map((x) => x.trim());
    }
  }

  return [];
}

// Map many possible inputs -> day numbers 0..6 (Sun=0..Sat=6)
function toDayNumber(d: any): number | null {
  if (typeof d === 'number' && Number.isFinite(d)) return d;

  const s = String(d ?? '').trim().toLowerCase();
  if (!s) return null;

  // numeric strings
  if (/^\d+$/.test(s)) return Number(s);

  // full day names / enum-ish strings
  if (s.includes('sun')) return 0;
  if (s.includes('mon')) return 1;
  if (s.includes('tue')) return 2;
  if (s.includes('wed')) return 3;
  if (s.includes('thu')) return 4;
  if (s.includes('fri')) return 5;
  if (s.includes('sat')) return 6;

  // shorthand
  if (s === 'su') return 0;
  if (s === 'm') return 1;
  if (s === 't') return 2; // Tue
  if (s === 'w') return 3;
  if (s === 'th') return 4;
  if (s === 'f') return 5;
  if (s === 'sa') return 6;

  return null;
}

function normalizeDaysToUi(input: any): number[] {
  const arr = coerceToArray(input);
  if (!arr.length) return [];

  const raw = arr
     .map(toDayNumber)
    .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));

  if (!raw.length) return [];

  // If values look like DB 1..7 (smallint[]), convert to UI 0..6
  // Legacy DB support (1..7 with Sunday=7) -> normalize to 0..6 (Sunday=0)
  const ui = raw.map((n) => (n === 7 ? 0 : n));
  
  return Array.from(new Set(ui.filter((n) => n >= 0 && n <= 6))).sort((a, b) => a - b);
}

function pickFirstNonEmptyUiDays(event: any): number[] {
  const candidates = [
    event?.active_days, // DB field
    event?.activeDays,  // camel version
    event?.days,        // UI/editor version
  ];

  for (const c of candidates) {
    const normalized = normalizeDaysToUi(c);
    if (normalized.length > 0) return normalized;
  }
  return [];
}

export const SpecialCard: React.FC<SpecialCardProps> = ({
  event,
  onClick,
  onFavoriteToggle,
  isFavorite,
  onCopyAddress,
  copiedId,
  hideFavorite = false,
  className = '',
  onGetVerified,
  distanceMiles,
}) => {
  const e: any = event as any;
  const isDemo = e.listing_status === 'demo';
  const isVerified = Boolean(e?.isVerified ?? e?.is_verified);
  const distanceLabel = formatDistance(distanceMiles ?? e.distanceMiles);
  const verificationBadge = isVerified
    ? { label: 'Verified Venue', className: 'bg-amber-400/95 text-black border border-amber-200 shadow-[0_10px_30px_rgba(245,158,11,0.25)]' }
    : { label: 'Preview Listing', className: 'bg-white/90 text-gray-700 border border-black/10' };
  const cardTone = isVerified
    ? {
        shell: 'border-amber-500 ring-amber-500/20 shadow-[0_30px_80px_-15px_rgba(245,158,11,0.3)] hover:shadow-2xl hover:-translate-y-2',
        dayOn: 'bg-amber-500 text-black',
        timePill: 'bg-amber-400 text-black',
        headline: 'text-amber-500',
        category: 'text-amber-600',
        price: 'text-amber-600',
        more: 'text-amber-600',
        vibesTitle: 'text-amber-600',
        addressHover: 'hover:text-amber-600',
      }
    : {
        shell: 'border-black/10 ring-black/5 shadow-[0_18px_45px_-20px_rgba(0,0,0,0.18)] hover:shadow-xl hover:-translate-y-1',
        dayOn: 'bg-black text-white',
        timePill: 'bg-black text-white',
        headline: 'text-black',
        category: 'text-gray-500',
        price: 'text-black',
        more: 'text-gray-500',
        vibesTitle: 'text-gray-500',
        addressHover: 'hover:text-black',
      };
  
  const deals = Array.isArray(e.deals) ? e.deals : [];
  const featuredDeals = deals.filter((d: any) => !!d.isFeatured);
  const dealsForCard = isVerified && featuredDeals.length > 0 ? featuredDeals : deals;
  const sortedDeals = [...dealsForCard].sort((a: any, b: any) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
  const maxCardDeals = isVerified ? MAX_VERIFIED_CARD_DEALS : MAX_PREVIEW_CARD_DEALS;
  const displayedDeals = sortedDeals.slice(0, maxCardDeals);
  const extraDealsCount = dealsForCard.length - displayedDeals.length;
  const specialHeadline = String(e.name || e.specialType || e.title || '').trim();

  const rawDisplayAddress =
    e.streetAddress && e.city && e.state && e.zipCode ? `${e.streetAddress}, ${e.city}, ${e.state} ${e.zipCode}` : e.address;
  const displayAddress = String(rawDisplayAddress || '').trim();
  const safeTags = Array.isArray(e.tags)
    ? e.tags.map((tag: any) => String(tag ?? '').trim()).filter(Boolean)
    : [];
  // ✅ FIX: actually pick the first NON-EMPTY days source
  const uiDays = pickFirstNonEmptyUiDays(e);

  return (
    <div
      onClick={onClick}
      className={`group relative rounded-[2.5rem] bg-white overflow-hidden transition-all duration-500 cursor-pointer border-4 ring-4 md:ring-8 flex flex-col h-full ${cardTone.shell} ${className}`}
    >
      <div className="relative h-64 overflow-hidden shrink-0">
        <img src={e.imageUrl} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={e.venueName} />

        <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
          {isDemo && (
            <span className="px-3 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] bg-gray-500/80 text-white backdrop-blur-md border border-white/20">
              Sample Listing
            </span>
          )}
          <span className={`px-3 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] shadow-lg ${verificationBadge.className}`}>
            {verificationBadge.label}
          </span>
          {distanceLabel && (
            <span className="px-3 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] bg-black/75 text-amber-300 backdrop-blur-md border border-amber-300/30 shadow-lg">
              {distanceLabel}
            </span>
          )}
        </div>

        {!hideFavorite && onFavoriteToggle && (
          <button
            onClick={onFavoriteToggle}
            className={`absolute top-4 right-4 w-10 h-10 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
              isFavorite ? 'bg-amber-500 text-black' : 'bg-white/20 text-white'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        )}

        <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {WEEKDAY_LABELS.map((label, idx) => {
              const dayNumber = LABEL_TO_DAY_NUMBER[idx];
              return (
              <div
                key={label}
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-black ${
                 uiDays.includes(dayNumber) ? cardTone.dayOn : 'bg-white/10 text-white/30'
                }`}
              >
                {label}
              </div>
            )})}
          </div>

          <div className={`font-black text-[9px] uppercase px-3 py-1.5 rounded-full ${cardTone.timePill}`}>
            {formatTo12Hour(e.startTime)} - {formatTo12Hour(e.endTime)}
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 flex flex-col h-full bg-[#FFFDF5]">
        <div className="space-y-2 mb-6 text-left">
          <h3 className="text-3xl font-bold serif tracking-tighter leading-none text-black truncate">{e.venueName}</h3>

          <button onClick={onCopyAddress} className={`flex items-center gap-1.5 text-gray-400 transition-colors group/addr ${cardTone.addressHover}`}>
            <span className="text-[9px] font-bold uppercase tracking-widest truncate">{copiedId === e.id ? 'Copied!' : displayAddress}</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 opacity-0 group-hover/addr:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2" />
            </svg>
          </button>

           </div>

        {specialHeadline && (
          <div className="mb-5 mt-1 text-left">
            <p className={`text-lg md:text-xl font-black italic leading-none tracking-tight drop-shadow-[0_1px_0_rgba(0,0,0,0.08)] ${cardTone.headline}`}>
              {specialHeadline}
            </p>
          </div>
        )}

        <div className="flex-1 space-y-4 mb-4 text-left">
          {displayedDeals.map((deal: any, index: number) => {
            const dealItem = String(deal?.item ?? deal?.name ?? deal?.title ?? deal?.label ?? deal?.description ?? '').trim();
            const dealCategory = String(deal?.category ?? '').trim();
            const dealPrice = String(deal?.price ?? deal?.value ?? deal?.amount ?? '').trim();

            return (
              <div key={deal.id || `${dealCategory}-${dealPrice}-${index}`} className={`flex justify-between items-end gap-3 animate-in slide-in-from-top-2 duration-300 ${deal.isGroupDeal ? 'py-1' : ''}`}>
                <div className="flex flex-col min-w-0">
                  <span className={`text-[7px] font-black uppercase ${cardTone.category}`}>{dealCategory}</span>
                  <span className={`text-sm truncate ${deal.isGroupDeal ? 'font-black italic' : 'font-bold'} text-black`}>{dealItem}</span>
                </div>
                <div className="flex-1 border-b border-dotted border-black/10 mb-1" />
                <span className={`text-xl font-black whitespace-nowrap leading-none text-right ${cardTone.price} ${deal.isGroupDeal ? 'tracking-tight' : ''}`}>{dealPrice}</span>
              </div>
              );
          })}

          {extraDealsCount > 0 && (
            <div className="pt-2">
              <span className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest group-hover:underline underline-offset-4 decoration-2 ${cardTone.more}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                </svg>
                See {extraDealsCount} more deals
              </span>
            </div>
          )}
        </div>

        {e.notes && (
          <div className="mb-4 text-left">
            <p className="text-[9px] text-gray-400 font-medium italic truncate">Note: {e.notes}</p>
          </div>
        )}

        {!isVerified && onGetVerified && (
          <div className="mb-2 pt-1 text-center">
            <p className="mb-2 text-[12px] font-semibold tracking-[0.04em] text-gray-500">
              Is this your venue?
            </p>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onGetVerified();
              }}
              className="inline-flex w-full items-center justify-center rounded-[1.1rem] border border-white/10 bg-black/75 px-5 py-3.5 text-[11px] font-black uppercase tracking-[0.18em] text-white shadow-[0_14px_30px_rgba(15,23,42,0.18)] backdrop-blur-sm transition-all hover:scale-[1.01] hover:bg-amber-500 hover:text-black hover:shadow-[0_16px_34px_rgba(245,158,11,0.24)] active:scale-[0.98] sm:w-auto"
            >
              Get Verified Now
            </button>
          </div>
        )}

        {isVerified && safeTags.length > 0 && (
          <div className="mt-auto pt-4 border-t border-black/[0.03] text-left">
            <h4 className={`text-[7px] font-black uppercase tracking-[0.2em] mb-2 ${cardTone.vibesTitle}`}>Vibes</h4>
            <div className="flex flex-wrap gap-1.5">
             {safeTags.map((tag) => (
                <span key={tag} className="px-2 py-1 rounded-md bg-gray-50 text-[7px] font-black uppercase tracking-widest text-gray-400 border border-black/[0.03]">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
